import {
    isPlayerAction,
    isRemovePlayerEvent,
    PlayerId,
    Projection,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import {shallow} from 'zustand/shallow';
import {isTransferRingEvent} from '../SupplyChainPayloadTypes';

export type TransferCountMap = Record<PlayerId, number>;

export interface PlayerActivitySlice extends SharedGameState {
    transferCounts?: TransferCountMap;
}

export const playerActivityProjection: Projection<PlayerActivitySlice> = {
    name: 'playerActivityProjection',
    project: (state, newEvents) => {
        const counts = {...(state.transferCounts ?? {})};
        let modified = false;

        newEvents.forEach(ev => {
            if (isPlayerAction(ev) && isTransferRingEvent(ev.payload)) {
                const payload = ev.payload;
                const src = payload.sourcePlayerId;
                counts[src] = (counts[src] ?? 0) + 1;
                modified = true;
            } else  if (isRemovePlayerEvent(ev)) {
                const removeEv = ev;
                const removedId = removeEv.removePlayerId;
                const replacementId = removeEv.replacementId;

                const removedCount = counts[removedId] ?? 0;

                if (removedId in counts) {
                    delete counts[removedId];
                    modified = true;
                }
                if (replacementId && removedCount > 0) {
                    counts[replacementId] = (counts[replacementId] ?? 0) + removedCount;
                    modified = true;
                }
            }
        });
        return modified ? {transferCounts: counts} : {};
    },
    reset: () => ({transferCounts: {}}),
};

/**
 * Derives the most‑active player (the player who transferred the most rings).
 * Returns `{ playerId, transferCount }` or `{ undefined, 0 }` when no data exists.
 */
const emptyCounts = {playerId: undefined, transferCount: 0};   // stable reference to prevent react-render churn
export const useMostActivePlayer = () => useGameStore((s: PlayerActivitySlice) => {
    const transferCounts =  s.transferCounts ?? {};
    const sorted = Object.entries(transferCounts).sort(
        ([, aCount], [, bCount]) => bCount - aCount,
    );

    return sorted.length > 0
        ? {playerId: sorted[0][0], transferCount: sorted[0][1]}
        : emptyCounts;
}, shallow);
